/**
 * lh-product.js — Lieblingshaus product page JS
 * Replaces lieblingshaus.js (1 MB Webflow bundle) on all product templates.
 *
 * Handles:
 *  1. w-mod-ix class  — needed so Webflow CSS states (opacity:0 guards) release
 *  2. Swiper gallery  — main image slider + thumbnail strip navigation
 *  3. Variant pills   — pill click → select sync → hidden ID → price/image/stock
 *  4. Accordion       — .product-header_heading click expands .product-header_details
 *  5. FAQ accordion   — .faq_question click expands .faq_answer + icon rotation
 *  6. Vollbild        — fullscreen image overlay open / close
 *  7. Qty ± buttons   — delegated handler (replaces duplicate inline scripts)
 *  8. Recommendations — Shopify Search & Discovery native section fetch
 */

(function () {
  'use strict';

  /* ─── 1. w-mod-ix ────────────────────────────────────────────────────────
   * lieblingshaus.js added this class, which gates certain CSS rules such as:
   *   html.w-mod-js:not(.w-mod-ix) [data-w-id="15c563cc-…"] { opacity:0; }
   * Without it the fullscreen button stays invisible.
   * ─────────────────────────────────────────────────────────────────────── */
  document.documentElement.classList.add('w-mod-ix');

  /* ─── 2. Variant selection ───────────────────────────────────────────────
   * product-variants-data.liquid renders a JSON blob INSIDE each form.
   * We parse it per-form so the page-level querySelector can't accidentally
   * pick up a companion-product form (e.g. product-passend-dazu) that
   * appears earlier in the DOM than the main product form.
   * ─────────────────────────────────────────────────────────────────────── */
  var forms = document.querySelectorAll('form[data-node-type="commerce-add-to-cart-form"]');

  forms.forEach(function (form) {
    // Parse this form's own variant JSON (scoped — avoids cross-form pollution)
    var variantData = [];
    var dataScript  = form.querySelector('script[data-type="product-variations-data"]');
    if (dataScript) {
      try { variantData = JSON.parse(dataScript.textContent); } catch (e) {
        console.error('[lh-product] Failed to parse variant data:', e);
      }
    }
    // Show pill groups, hide native selects, mark first pill selected
    initPills(form);
    // Reflect initial selection → price / image / stock
    refreshVariant(form, variantData);

    // Delegate pill clicks inside this form
    form.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-node-type="add-to-cart-option-pill"]');
      if (!pill) return;

      var group = pill.closest('[data-node-type="add-to-cart-option-pill-group"]');
      if (!group) return;

      // Toggle selected state within this group
      group.querySelectorAll('[data-node-type="add-to-cart-option-pill"]').forEach(function (p) {
        p.classList.remove('w--ecommerce-pill-selected');
        p.setAttribute('aria-checked', 'false');
      });
      pill.classList.add('w--ecommerce-pill-selected');
      pill.setAttribute('aria-checked', 'true');

      // Keep the hidden <select> in sync so FormData picks up the right value
      var fieldset = pill.closest('fieldset[data-node-type="add-to-cart-option"]');
      var sel      = fieldset && fieldset.querySelector('select[data-node-type="add-to-cart-option-select"]');
      if (sel) sel.value = pill.dataset.optionName;

      refreshVariant(form, variantData);
    });

    // Also listen to native select changes (house-in-style / leinen templates
    // may still use visible selects; belt-and-suspenders for all templates)
    form.querySelectorAll('select[data-node-type="add-to-cart-option-select"]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        // Sync pill selection to match the select
        var fieldset  = sel.closest('fieldset[data-node-type="add-to-cart-option"]');
        var group     = fieldset && fieldset.querySelector('[data-node-type="add-to-cart-option-pill-group"]');
        if (group) {
          group.querySelectorAll('[data-node-type="add-to-cart-option-pill"]').forEach(function (p) {
            var isSelected = p.dataset.optionName === sel.value;
            p.classList.toggle('w--ecommerce-pill-selected', isSelected);
            p.setAttribute('aria-checked', String(isSelected));
          });
        }
        refreshVariant(form, variantData);
      });
    });
  });

  /**
   * On page load, show pill groups (hide native selects) and mark the pill
   * that matches each select's current value. Falls back to the first pill.
   */
  function initPills(form) {
    form.querySelectorAll('fieldset[data-node-type="add-to-cart-option"]').forEach(function (fieldset) {
      var group = fieldset.querySelector('[data-node-type="add-to-cart-option-pill-group"]');
      var sel   = fieldset.querySelector('select[data-node-type="add-to-cart-option-select"]');

      if (!group) return; // no pill UI for this option — leave select as-is

      // Ensure pill UI is visible (some templates start with display:none)
      group.style.display = 'flex';
      // Hide native select (it stays in DOM for FormData sync)
      if (sel) sel.style.display = 'none';

      var pills   = group.querySelectorAll('[data-node-type="add-to-cart-option-pill"]');
      if (!pills.length) return;

      var current = sel ? sel.value : '';
      var target  = null;
      pills.forEach(function (p) {
        if (p.dataset.optionName === current) target = p;
      });
      if (!target) target = pills[0];

      // Deselect all, then select the target
      pills.forEach(function (p) {
        p.classList.remove('w--ecommerce-pill-selected');
        p.setAttribute('aria-checked', 'false');
      });
      if (target) {
        target.classList.add('w--ecommerce-pill-selected');
        target.setAttribute('aria-checked', 'true');
        if (sel) sel.value = target.dataset.optionName;
      }
    });
  }

  /** Read which option value is selected in each option-select within a form. */
  function getSelectedOptions(form) {
    var opts = {};
    var idx  = 1;
    form.querySelectorAll('select[data-node-type="add-to-cart-option-select"]').forEach(function (sel) {
      opts['option' + idx] = sel.value;
      idx++;
    });
    return opts;
  }

  /** Find the first variant whose option1–3 all match the given selection map. */
  function findVariant(variantData, opts) {
    return variantData.find(function (v) {
      return Object.keys(opts).every(function (key) {
        return !opts[key] || v[key] === opts[key];
      });
    }) || null;
  }

  /**
   * After a pill click (or on init), resolve the active variant and update:
   *  - hidden input[name="id"] in the form
   *  - ALL [data-commerce-type="variation-price"] on the page
   *  - ALL [data-commerce-type="unit-price"] on the page
   *  - ALL [data-commerce-type="variation-image"] on the page
   *  - Swiper slide (slides to the variant's featured image)
   *  - Out-of-stock state in the form's wrapper
   */
  function refreshVariant(form, variantData) {
    if (!variantData.length) return;

    var opts    = getSelectedOptions(form);
    var variant = findVariant(variantData, opts);
    if (!variant) return;

    // Update hidden variant ID input
    var idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;

    // Update all price displays (page-wide — covers main + fixed-CTA)
    document.querySelectorAll('[data-commerce-type="variation-price"]').forEach(function (el) {
      el.textContent = variant.price;
    });

    // Update unit price displays
    // unit_price is raw HTML from unit-price.liquid, json-encoded without | escape
    // so JSON.parse gives us actual '<span>' characters → innerHTML creates real elements
    document.querySelectorAll('[data-commerce-type="unit-price"]').forEach(function (el) {
      el.innerHTML = variant.unit_price || '';
    });

    // Update variation images (vollbild full-screen img, fixed-CTA product img)
    if (variant.featured_image) {
      document.querySelectorAll('[data-commerce-type="variation-image"]').forEach(function (img) {
        img.src = variant.featured_image;
      });
      // Slide gallery to the matching image
      slideGalleryToImage(variant.featured_image);
    }

    // Resolve out-of-stock state within the nearest form wrapper
    var wrap = form.closest('.add-to-cart_wrapper')  ||
               form.closest('.erganzung_addtocart')   ||
               form.closest('.fixed-addtocart')       ||
               form.parentElement;
    var oos  = wrap && wrap.querySelector('.w-commerce-commerceaddtocartoutofstock');
    var btn  = form.querySelector('[data-node-type="commerce-add-to-cart-button"]');

    // Alternative-color soldout snippet: sibling of .add-to-cart_wrapper
    var parentEl    = wrap && wrap.parentElement;
    var soldoutWrap = parentEl && parentEl.querySelector('[data-soldout-wrapper]');
    var lager          = wrap && wrap.querySelector('.product_lager-wrapper');
    var lagerText      = lager && lager.querySelector('.product-lager_text');
    var ptpNotice      = wrap && wrap.querySelector('.ptp-notice');
    var qtyWrapper     = wrap && wrap.querySelector('.quantity-wrapper');

    if (variant.available) {
      if (oos) oos.style.display = 'none';
      if (soldoutWrap) soldoutWrap.style.display = 'none';
      if (btn) { btn.style.display = ''; btn.disabled = false; btn.setAttribute('aria-busy', 'false'); }
      if (lager) {
        var isInStock = variant.inventory_management === 'shopify' && variant.inventory_quantity > 0;
        var lagerIsPtp = lager.dataset.ptp === 'true';
        if (isInStock) {
          lager.style.display = '';
          lager.classList.add('is-instock');
          if (lagerText) lagerText.textContent = lager.dataset.instockText || 'Sofort verfügbar';
        } else if (lagerIsPtp) {
          // PTP: kein grauer "Lieferzeit"-Badge — der Eingestellt-Banner trägt die Info
          lager.style.display = 'none';
        } else {
          lager.style.display = '';
          lager.classList.remove('is-instock');
          if (lagerText) lagerText.textContent = lager.dataset.defaultText || '';
        }
      }
      if (qtyWrapper) qtyWrapper.style.display = '';
      if (ptpNotice) ptpNotice.style.display = '';
    } else {
      if (soldoutWrap) {
        // Rich soldout snippet — hide button, qty and stock indicator
        soldoutWrap.style.display = '';
        if (oos) oos.style.display = 'none';
        if (btn) { btn.style.display = 'none'; btn.disabled = true; btn.setAttribute('aria-busy', 'false'); }
        if (lager) lager.style.display = 'none';
        if (ptpNotice) ptpNotice.style.display = 'none';
        if (qtyWrapper) qtyWrapper.style.display = 'none';
      } else {
        if (oos) oos.style.display = '';
        if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'false'); }
      }
    }
  }

  /* ─── 4. Accordion ───────────────────────────────────────────────────────
   * .product-header_heading (with data-w-id) is the click trigger.
   * Its next sibling .product-header_details starts at style="height:0px".
   * We animate height 0 ↔ scrollHeight with a CSS transition.
   * ─────────────────────────────────────────────────────────────────────── */
  document.querySelectorAll('.product-header_heading').forEach(function (heading) {
    var details = heading.nextElementSibling;
    if (!details || !details.classList.contains('product-header_details')) return;

    // Enable smooth animation
    details.style.overflow   = 'hidden';
    details.style.transition = 'height 0.28s ease';
    heading.style.cursor     = 'pointer';

    heading.addEventListener('click', function () {
      var isOpen = details.style.height !== '' && details.style.height !== '0px';
      if (isOpen) {
        details.style.height = '0px';
      } else {
        details.style.height = details.scrollHeight + 'px';
      }
    });
  });

  /* ─── 5. FAQ accordion ───────────────────────────────────────────────────
   * .faq_question is the click trigger.
   * Its next sibling .faq_answer starts at style="height:0px".
   * .faq_icon (img inside .faq_question) rotates 45° when open.
   * ─────────────────────────────────────────────────────────────────────── */
  document.querySelectorAll('.faq_question').forEach(function (question) {
    var answer = question.nextElementSibling;
    if (!answer || !answer.classList.contains('faq_answer')) return;

    var icon = question.querySelector('.faq_icon');

    answer.style.transition = 'height 0.28s ease';
    if (icon) icon.style.transition = 'transform 0.28s ease';

    question.addEventListener('click', function () {
      var isOpen = answer.style.height !== '' && answer.style.height !== '0px';
      if (isOpen) {
        answer.style.height = '0px';
        if (icon) icon.style.transform = 'rotate(0deg)';
      } else {
        answer.style.height = answer.scrollHeight + 'px';
        if (icon) icon.style.transform = 'rotate(45deg)';
      }
    });
  });

  /* ─── 6. Vollbild (fullscreen overlay) ──────────────────────────────────
   * .is-fullscreen-button → show .vollbild_wrapper
   * .vollbild-close_wrapper  → hide .vollbild_wrapper
   * ─────────────────────────────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    if (e.target.closest('.is-fullscreen-button')) {
      e.preventDefault();
      var overlay = document.querySelector('.vollbild_wrapper');
      if (overlay) overlay.style.display = 'block';
      return;
    }
    if (e.target.closest('.vollbild-close_wrapper')) {
      e.preventDefault();
      var overlay2 = document.querySelector('.vollbild_wrapper');
      if (overlay2) overlay2.style.display = 'none';
    }
  });

  /* ─── 7. Qty ± buttons ───────────────────────────────────────────────────
   * Delegated listener replaces the inline scripts in some product templates.
   * ─────────────────────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var btn = e.target;
    if (btn.classList.contains('qty-minus')) {
      var inputMinus = btn.nextElementSibling;
      if (inputMinus && inputMinus.type === 'number') {
        var vMinus = parseInt(inputMinus.value, 10);
        if (vMinus > 1) {
          inputMinus.value = vMinus - 1;
          inputMinus.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      return;
    }
    if (btn.classList.contains('qty-plus')) {
      var inputPlus = btn.previousElementSibling;
      if (inputPlus && inputPlus.type === 'number') {
        var vPlus = parseInt(inputPlus.value, 10);
        inputPlus.value = vPlus + 1;
        inputPlus.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  /* ─── 8. Product recommendations (Search & Discovery) ──────────────────
   * sections/product-grid.liquid renders empty on first load because
   * recommendations.performed = false at server-render time.
   * We fetch the same section URL with the current product_id so Shopify
   * populates the recommendations object, then swap in the result.
   * ─────────────────────────────────────────────────────────────────────── */
  var recWrapper = document.querySelector('[data-product-recommendations-url]');
  if (recWrapper) {
    fetch(recWrapper.dataset.productRecommendationsUrl)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc   = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-product-recommendations-url]');
        if (fresh && fresh.innerHTML.trim()) {
          recWrapper.innerHTML = fresh.innerHTML;
        }
      })
      .catch(function (err) {
        console.warn('[lh-product] Recommendations fetch failed:', err);
      });
  }

  /* ─── 7. Swiper gallery ──────────────────────────────────────────────────
   * swiper-bundle.min.js is loaded with `defer` in theme.liquid, so it
   * executes AFTER HTML parsing — before DOMContentLoaded.  We must wait
   * for DOMContentLoaded before window.Swiper is defined.
   * ─────────────────────────────────────────────────────────────────────── */
  var mainSwiper    = null;
  var imageIndexMap = {}; // data-large URL → slide index

  // Build map from thumbnail strip (elements already in DOM at script run time)
  document.querySelectorAll('.thumbnail img[data-index]').forEach(function (img) {
    var idx   = parseInt(img.dataset.index, 10);
    var large = img.dataset.large || '';
    if (!isNaN(idx) && large) imageIndexMap[large] = idx;
  });

  document.addEventListener('DOMContentLoaded', function () {
    // By DOMContentLoaded, all defer scripts (incl. swiper-bundle) have run
    if (!window.Swiper) {
      console.warn('[lh-product] Swiper not available at DOMContentLoaded');
      return;
    }

    var swiperEl = document.querySelector('.swiper-container.main-image');
    if (swiperEl) {
      mainSwiper = new Swiper(swiperEl, {
        slidesPerView : 1,
        loop          : false,
        grabCursor    : true,
      });
    }

    // ── Active thumbnail border ───────────────────────────────────────────────
    var thumbImgs = Array.from(document.querySelectorAll('.thumbnail img[data-index]'));

    function setActiveThumbnail(idx) {
      thumbImgs.forEach(function (img) {
        img.classList.toggle('active', parseInt(img.dataset.index, 10) === idx);
      });
    }

    // Highlight slide 0 on init
    if (thumbImgs.length) setActiveThumbnail(0);

    // Keep border in sync whenever the main slide changes (swipe or slideTo)
    if (mainSwiper) {
      mainSwiper.on('slideChange', function () {
        setActiveThumbnail(mainSwiper.activeIndex);
      });
    }

    // Thumbnail click → slide to matching index
    thumbImgs.forEach(function (img) {
      img.style.cursor = 'pointer';
      img.addEventListener('click', function () {
        var idx = parseInt(img.dataset.index, 10);
        if (!isNaN(idx) && mainSwiper) mainSwiper.slideTo(idx);
        // setActiveThumbnail is handled by the slideChange event above
      });
    });
  });

  /**
   * Slide the gallery to whichever slide contains the given image URL.
   * Shopify image URLs include size suffixes (_master, _1024x, …) so we
   * match on the base filename (everything before the last underscore segment).
   */
  function slideGalleryToImage(imageUrl) {
    if (!mainSwiper) return;

    // Try exact match first
    var idx = imageIndexMap[imageUrl];

    // Fall back: strip Shopify size suffix and compare base filenames
    if (idx === undefined) {
      var base = stripSizeSuffix(imageUrl);
      Object.keys(imageIndexMap).forEach(function (key) {
        if (stripSizeSuffix(key) === base) idx = imageIndexMap[key];
      });
    }

    if (idx !== undefined && !isNaN(idx)) mainSwiper.slideTo(idx);
  }

  function stripSizeSuffix(url) {
    // Remove query string, then strip trailing _NNNx or _master etc.
    return url.split('?')[0].replace(/_[a-z0-9]+x[a-z0-9]*$|_master$/, '');
  }

})();
